import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import DynamicPhoneNumber from "common/blocks/DynamicPhoneNumber";
import Info from "common/blocks/Info";
import InfoButton from "common/blocks/InfoButton";
import RichText from "common/blocks/RichText";
import UIIcon from "common/blocks/UIIcon";
import useModalContext from "common/providers/modal/useModalContext";
import { valuationRangeModal } from "features/vehicleDetails/modals";
import buildRichtextPopUp from "features/vehicleDetails/modals/RichTextPopup";
import usePartExchangeQuestionsContext from "features/vehicleDetails/providers/partExchangeQuestions/usePartExchangeQuestionsContext";
import { ResolvedHigherOfferSidePanel } from "resolvers/data/higherOfferSidePanelResolver";
import type { ResolvedPopup } from "resolvers/data/popupResolver";
import type { ResolvedRichText } from "resolvers/data/richTextResolver";
import type { PartExchange as IPartExchange, PartExchange } from "services/motorpoint/partExchange";
import formatCurrency from "utils/formatCurrency";

import styles from "../CalculatorStats.module.scss";

const cx = classNames.bind(styles);

export type PartExchangeDetailsProps = {
    partExchange?: IPartExchange;
    isPxMoreThanMaxDeposit?: boolean;
    partExchangeEquityMoreThanMaxDepositMessage: ResolvedRichText;
    whatIsPXPopup: ResolvedPopup;
    enquiryLink: string;
    higherOfferLabel: string;
    higherOfferButtonText: string;
    higherOfferSidePanel: ResolvedHigherOfferSidePanel;
};

const PartExchangeDetails = ({
    partExchange,
    isPxMoreThanMaxDeposit,
    partExchangeEquityMoreThanMaxDepositMessage,
    whatIsPXPopup,
    enquiryLink,
    higherOfferLabel,
    higherOfferButtonText,
    higherOfferSidePanel,
}: PartExchangeDetailsProps) => {
    const { partExQuestionsAnswersResponse } = usePartExchangeQuestionsContext();
    const { usePopup, useSidebar } = useModalContext();
    const { openPopup } = usePopup();
    const { openSidebar } = useSidebar();

    const valuation = partExchange?.Valuation || partExQuestionsAnswersResponse?.Valuation;
    const equity = partExchange?.Equity || partExQuestionsAnswersResponse?.Equity || 1;
    const valuationRange =
        partExchange?.ValuationRange || partExQuestionsAnswersResponse?.ValuationRange;

    const onInfoButtonClick = () => openPopup(buildRichtextPopUp(whatIsPXPopup));

    const openValuationRangeSidebar = () =>
        openSidebar(
            valuationRangeModal({
                higherOfferSidePanel,
                partExchange: partExQuestionsAnswersResponse as PartExchange,
            })
        );

    return (
        <>
            <div className={cx("paymentDetails", "partExchange")}>
                <div className={cx("paymentDetailsInner")}>
                    <InfoButton onClick={onInfoButtonClick} />
                    <div className={cx("detail")}>
                        <span
                            className={cx("amount")}
                            dangerouslySetInnerHTML={{
                                __html: formatCurrency(valuation ?? 0, 2),
                            }}
                        />
                        <span className={cx("description")}>Part exchange value</span>
                    </div>
                    <div className={cx("detail")}>
                        <span
                            className={cx("amount", "small")}
                            dangerouslySetInnerHTML={{
                                __html: formatCurrency(equity, 2),
                            }}
                        />
                        <span className={cx("description")}>Part exchange equity</span>
                    </div>
                </div>
                {!!valuationRange?.MaximumValuation && (
                    <div className={cx("offerMore")}>
                        <span className={cx("label")}>{higherOfferLabel}</span>
                        <div className={cx("linkButtonWrapper")}>
                            <Button
                                theme="textLink"
                                ariaLabel={higherOfferButtonText}
                                onClick={openValuationRangeSidebar}
                            >
                                {higherOfferButtonText}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
            {isPxMoreThanMaxDeposit && (
                <>
                    <Info>
                        <RichText {...partExchangeEquityMoreThanMaxDepositMessage} />
                    </Info>
                    <Button
                        href={enquiryLink}
                        rel="noreferrer noopener nofollow"
                        target="_blank"
                        theme="secondary"
                        fullWidth
                        className={cx("button")}
                        ariaLabel=" Make enquiry"
                    >
                        <UIIcon icon="Mail" className={cx("mailIcon")} /> Make enquiry
                    </Button>
                    <div className={cx("contactDetails")}>
                        <UIIcon icon="Tel" className={cx("phoneIcon")} />
                        <DynamicPhoneNumber isLink />
                    </div>
                </>
            )}
        </>
    );
};

export default PartExchangeDetails;
